import { useCallback, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, CheckSquare, Trash2, Calendar, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const TasksPage = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newTask, setNewTask] = useState('');
  const [adding, setAdding] = useState(false);
  const { token } = useAuth();

  const fetchTasks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/tasks`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const addTask = async (e) => {
    e.preventDefault();
    if (!newTask.trim()) return;
    
    setAdding(true);
    try {
      await axios.post(`${API}/tasks`, 
        { title: newTask, priority: 'medium' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewTask('');
      fetchTasks();
    } catch (error) {
      console.error('Error adding task:', error);
    } finally {
      setAdding(false);
    }
  };

  const toggleTask = async (taskId, completed) => {
    try {
      await axios.patch(`${API}/tasks/${taskId}`, 
        { completed: !completed },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchTasks();
    } catch (error) {
      console.error('Error toggling task:', error);
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const pendingTasks = tasks.filter(t => !t.completed);
  const completedTasks = tasks.filter(t => t.completed);

  return (
    <div className="min-h-screen bg-[#1A1A2E] px-4 py-6" data-testid="tasks-page">
      <div className="max-w-4xl mx-auto">
        <Link 
          to="/dashboard" 
          className="inline-flex items-center gap-2 text-[#94A3B8] hover:text-[#7EC8C8] mb-6 transition-colors"
        >
          <ArrowLeft size={18} />
          <span>Retour au dashboard</span>
        </Link>

        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-2xl bg-[#4ECDC4]/20 flex items-center justify-center">
              <CheckSquare className="w-6 h-6 text-[#4ECDC4]" />
            </div>
            <h1 
              className="text-3xl font-bold text-[#F8F9FA]"
              style={{ fontFamily: 'Outfit' }}
            >
              Suivi des tâches
            </h1>
          </div>
          <p className="text-[#94A3B8] mb-8">
            Organise tes démarches administratives et ne rate plus aucune deadline.
          </p>
        </motion.div>

        {/* Add task form */}
        <motion.form
          onSubmit={addTask}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="flex gap-3 mb-8"
        >
          <Input
            value={newTask}
            onChange={(e) => setNewTask(e.target.value)}
            placeholder="Ajouter une nouvelle tâche..."
            data-testid="new-task-input"
            className="flex-1 bg-[#F8F9FA]/5 border-[#7EC8C8]/20 text-[#F8F9FA] placeholder:text-[#94A3B8]/50"
          />
          <Button
            type="submit"
            disabled={adding || !newTask.trim()}
            data-testid="add-task-btn"
            className="bg-[#4ECDC4] hover:bg-[#7EC8C8] text-[#1A1A2E]"
          >
            {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          </Button>
        </motion.form>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-[#7EC8C8]" />
          </div>
        ) : (
          <>
            {/* Pending tasks */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="mb-8"
            >
              <h2 className="text-lg font-semibold text-[#F8F9FA] mb-4" style={{ fontFamily: 'Outfit' }}>
                À faire ({pendingTasks.length})
              </h2>
              {pendingTasks.length === 0 ? (
                <Card className="bg-[#1A1A2E]/50 border-[#7EC8C8]/20 border-dashed p-8 text-center">
                  <p className="text-[#94A3B8]">Aucune tâche en attente. Bravo ! 🎉</p>
                </Card>
              ) : (
                <div className="space-y-3">
                  {pendingTasks.map((task) => (
                    <Card 
                      key={task.id}
                      className="bg-[#1A1A2E]/50 border-[#7EC8C8]/20 p-4 hover:border-[#7EC8C8]/40 transition-all"
                    >
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => toggleTask(task.id, task.completed)}
                          className="w-6 h-6 rounded-full border-2 border-[#4ECDC4] hover:bg-[#4ECDC4]/20 transition-colors"
                        />
                        <div className="flex-1">
                          <p className="text-[#F8F9FA]">{task.title}</p>
                          {task.due_date && (
                            <p className="text-xs text-[#94A3B8] flex items-center gap-1 mt-1">
                              <Calendar className="w-3 h-3" />
                              {task.due_date}
                            </p>
                          )}
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          task.priority === 'high' ? 'bg-[#FF6B6B]/20 text-[#FF6B6B]' :
                          task.priority === 'medium' ? 'bg-[#7EC8C8]/20 text-[#7EC8C8]' :
                          'bg-[#94A3B8]/20 text-[#94A3B8]'
                        }`}>
                          {task.priority === 'high' ? 'Urgent' : task.priority === 'medium' ? 'Normal' : 'Bas'}
                        </span>
                        <button
                          onClick={() => deleteTask(task.id)}
                          className="p-2 text-[#94A3B8] hover:text-[#FF6B6B] transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </motion.div>

            {/* Completed tasks */}
            {completedTasks.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <h2 className="text-lg font-semibold text-[#94A3B8] mb-4" style={{ fontFamily: 'Outfit' }}>
                  Terminées ({completedTasks.length})
                </h2>
                <div className="space-y-3">
                  {completedTasks.map((task) => (
                    <Card 
                      key={task.id}
                      className="bg-[#1A1A2E]/30 border-[#7EC8C8]/10 p-4"
                    >
                      <div className="flex items-center gap-4">
                        <button
                          onClick={() => toggleTask(task.id, task.completed)}
                          className="w-6 h-6 rounded-full bg-[#4ECDC4] flex items-center justify-center"
                        >
                          <span className="text-[#1A1A2E] text-sm">✓</span>
                        </button>
                        <p className="text-[#94A3B8] line-through flex-1">{task.title}</p>
                        <button
                          onClick={() => deleteTask(task.id)}
                          className="p-2 text-[#94A3B8] hover:text-[#FF6B6B] transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </Card>
                  ))}
                </div>
              </motion.div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TasksPage;
